import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    Container,
    Grid,
    TextField,
    Typography,
    MenuItem,
    IconButton,
    Divider,
    useTheme,
    CircularProgress,
    Checkbox,
    FormControlLabel,
    Breadcrumbs,
    Link,
    ToggleButton,
    ToggleButtonGroup,
} from "@mui/material";
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { useNavigate, useParams } from "react-router-dom";
import { Formik, Form, ErrorMessage, FieldArray, getIn, useFormikContext } from "formik";
import * as Yup from "yup";
import { useAuth } from "../../../helpers/AuthProvider";
import CompulsoryFieldsLabel from "../../../helpers/CompulsoryFields";
import queryGraphql from "../../../helpers/ApiInterface";
import MiniBot from "../../MiniBot";
import MainDrawer from "../../MainDrawer";

// --- Data for Dropdowns ---
const providerDisplayData = [
    { value: 'gcp', label: 'Google Cloud Platform', icon: '/assets/gcp_mini_icon.svg' },
    { value: 'aws', label: 'Amazon Web Services', icon: '/assets/aws_mini_icon.svg', disabled: true},
    { value: 'azure', label: 'Microsoft Azure', icon: '/assets/azure_mini_icon.svg', disabled: true }
];
// Note: 'location' is used instead of 'region' for DataLakes
const gcpLocationList = [ "africa-south1", "asia-east1", "asia-east2", "asia-northeast1", "asia-northeast2", "asia-northeast3", "asia-south1", "asia-south2", "asia-southeast1", "asia-southeast2", "australia-southeast1", "australia-southeast2", "europe-central2", "europe-north1", "europe-southwest1", "europe-west1", "europe-west10", "europe-west12", "europe-west2", "europe-west3", "europe-west4", "europe-west6", "europe-west8", "europe-west9", "me-central1", "me-central2", "me-west1", "northamerica-northeast1", "northamerica-northeast2", "southamerica-east1", "southamerica-west1", "us-central1", "us-east1", "us-east4", "us-east5", "us-south1", "us-west1", "us-west2", "us-west3", "us-west4"];
const awsLocationList = [ "us-east-1", "us-east-2", "us-west-1", "us-west-2", "af-south-1", "ap-east-1", "ap-south-1", "ap-northeast-1", "ap-northeast-2", "ap-northeast-3", "ap-southeast-1", "ap-southeast-2", "ca-central-1", "eu-central-1", "eu-west-1", "eu-west-2", "eu-south-1", "eu-west-3", "eu-north-1", "me-south-1", "sa-east-1"];
const azureLocationList = ["eastus", "eastus2", "westus", "westus2", "westus3", "centralus", "northcentralus", "southcentralus", "canadacentral", "canadaeast", "uksouth", "ukwest", "northeurope", "westeurope", "francecentral", "germanywestcentral", "japaneast", "japanwest", "koreacentral", "koreasouth", "australiaeast", "australiasoutheast", "southafricanorth", "eastasia", "southeastasia", "brazilsouth", "uaenorth"];

// --- Interfaces ---
interface CostLabelPair { id?: string; cost: string; label: string; }
// Updated Form values for DataLakes
interface DataLakeFormValues { // Changed interface name
    id?: string;
    name: string;
    cloudProvider: string;
    projectId: string;
    environment: string;
    location: string;
    description: string;
    costCenterValue: string;
    costLabelPairs: CostLabelPair[];
}
interface ConfigData { gcpProjectId: string; }
interface FetchedDataLakeData { id: string; name: string; } // Changed interface name
interface AddDataLakesPageProps { isEdit: boolean; } // Changed interface name
interface DataLakesFormContentProps { isEdit: boolean; } // Changed interface name
// Simplified Dynamic Field Config for DataLakes
interface DynamicFieldConfig {
    labels: { projectId: string; };
    dropdowns: { locations: string[]; };
    defaults: { location: string; }
}

// --- Component to handle dynamic field updates ---
interface DynamicFieldsUpdaterProps {
    isEdit: boolean;
    initialFormValuesForEdit: DataLakeFormValues | null; // Changed interface name
    configData: ConfigData | null;
    setDynamicFields: React.Dispatch<React.SetStateAction<DynamicFieldConfig>>;
}

const DynamicFieldsUpdater: React.FC<DynamicFieldsUpdaterProps> = ({ isEdit, initialFormValuesForEdit, configData, setDynamicFields }) => {
    const { values, setFieldValue } = useFormikContext<DataLakeFormValues>(); // Changed interface name

    useEffect(() => {
        let config: DynamicFieldConfig;
        switch (values.cloudProvider) {
            case 'aws':
                config = { labels: { projectId: 'Account ID' }, dropdowns: { locations: awsLocationList }, defaults: { location: 'us-east-1' } };
                break;
            case 'azure':
                config = { labels: { projectId: 'Subscription ID' }, dropdowns: { locations: azureLocationList }, defaults: { location: 'eastus' }};
                break;
            case 'gcp':
            default:
                config = { labels: { projectId: 'Project ID' }, dropdowns: { locations: gcpLocationList }, defaults: { location: 'asia-south1' }};
                break;
        }
        setDynamicFields(config);

        if (!isEdit || (initialFormValuesForEdit && values.cloudProvider !== initialFormValuesForEdit.cloudProvider)) {
            setFieldValue('location', config.defaults.location);
            setFieldValue('projectId', values.cloudProvider === 'gcp' ? (configData?.gcpProjectId || '') : '');
        }
    }, [values.cloudProvider, setFieldValue, isEdit, initialFormValuesForEdit, configData, setDynamicFields]);

    return null;
};

// --- Main Form Content Component ---
const DataLakesFormContent: React.FC<DataLakesFormContentProps> = ({ isEdit }) => { // Changed component name
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const [isSubmittingForm, setIsSubmittingForm] = useState(false);
    const [configData, setConfigData] = useState<ConfigData | null>(null);
    const [confirmationChecked, setConfirmationChecked] = useState(false);
    const [initialFormValuesForEdit, setInitialFormValuesForEdit] = useState<DataLakeFormValues | null>(null); // Changed interface name
    const [isLoadingDataLakeData, setIsLoadingDataLakeData] = useState(false); // Changed state variable name
    const { user } = useAuth();
    const isViewer = user.roles?.some((role: string) => role === "viewer") || false;

    const [dynamicFields, setDynamicFields] = useState<DynamicFieldConfig>({
        labels: { projectId: 'Project ID' },
        dropdowns: { locations: gcpLocationList },
        defaults: { location: 'asia-south1' }
    });

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const configRes = await queryGraphql(`query GetConfig { getConfig { gcpProjectId } }`);
                if (configRes.getConfig) setConfigData(configRes.getConfig);
            } catch (error) {
                console.error("Error fetching initial data:", error);
            }
        };
        fetchInitialData();
    }, []);

    useEffect(() => {
        const fetchDataLakeForEdit = async () => { // Changed function name
            if (isEdit && id) {
                setIsLoadingDataLakeData(true); // Changed state variable name
                try {
                    const response = await queryGraphql(`
                        query GetDataplexLake($getDataplexLakeId: String!) { # Changed query name
                          getDataplexLake(id: $getDataplexLakeId) { # Changed field name
                            id name cloudProvider costLabelPairs { cost label }
                            details { 
                              dataplexLake { # Changed from dataSources
                                projectId location description
                              } 
                            }
                          }
                        }
                    `, { getDataplexLakeId: id }); // Changed variable name

                    const dataLake = response.getDataplexLake; // Changed field name
                    if (dataLake) {
                        const allPairs = dataLake.costLabelPairs || [];
                        const costCenterPair = allPairs.find((p: any) => p.cost === "cost-center");
                        const dynamicPairs = allPairs.filter((p: any) => p.cost !== "cost-center").map((pair: any, index: number) => ({ ...pair, id: `clp-edit-${index}` }));
                        const dlDetails = dataLake.details.dataplexLake; // Changed variable name and field
                        
                        setInitialFormValuesForEdit({
                            id: dataLake.id,
                            name: dataLake.name,
                            costCenterValue: costCenterPair ? costCenterPair.label : "",
                            costLabelPairs: dynamicPairs,
                            cloudProvider: dataLake.cloudProvider || 'gcp',
                            projectId: dlDetails.projectId || "",
                            environment: dataLake.environment || "dev",
                            location: dlDetails.location || "",
                            description: dlDetails.description || "",
                        });
                    } else {
                        navigate("/datasolutions/data-lakes"); // Changed navigation path
                    }
                } catch (err) {
                    console.error("Error fetching data lake for editing:", err); // Changed text
                    navigate("/datasolutions/data-lakes"); // Changed navigation path
                } finally {
                    setIsLoadingDataLakeData(false); // Changed state variable name
                }
            }
        };
        fetchDataLakeForEdit(); // Changed function name
    }, [isEdit, id, navigate]);

    const validationSchema = Yup.object().shape({
        name: Yup.string().required("This field is required").max(50, "Max 50 characters"),
        cloudProvider: Yup.string().required("This field is required"),
        projectId: Yup.string().required("This field is required"),
        environment: Yup.string().required("This field is required"),
        location: Yup.string().required("This field is required"),
        description: Yup.string().optional(),
        costCenterValue: Yup.string().required("Cost Center value is required").max(63, "Max 63 characters"),
        costLabelPairs: Yup.array().of(Yup.object().shape({
            cost: Yup.string().required("Key is required").max(63, "Max 63 characters").notOneOf(["cost-center"], "'cost-center' is a reserved key."),
            label: Yup.string().required("Value is required").max(63, "Max 63 characters"),
        })),
    });

    const defaultInitialValues: DataLakeFormValues = { // Changed interface name
        name: "",
        cloudProvider: "gcp",
        projectId: configData?.gcpProjectId || "",
        environment: "dev",
        location: "asia-south1",
        description: "",
        costCenterValue: "",
        costLabelPairs: [],
    };
    
    if (isEdit && isLoadingDataLakeData) { // Changed state variable name
        return <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: "50vh" }}><CircularProgress /><Typography sx={{ ml: 2 }}>Loading data lake...</Typography></Box>; // Changed text
    }
    if (isEdit && !initialFormValuesForEdit) {
         return <Typography color="error" sx={{p:3}}>Could not load data lake for editing.</Typography>; // Changed text
    }

    return (
        <Container sx={{ py: 3, px: { xs: 2, sm: 3, md: 4 }, flexGrow: 1 }}>
            <Formik
                initialValues={isEdit ? (initialFormValuesForEdit || defaultInitialValues) : defaultInitialValues}
                validationSchema={validationSchema}
                enableReinitialize={true}
                onSubmit={async (values, { setSubmitting, setStatus }) => {
                    setIsSubmittingForm(true);
                    setSubmitting(true);

                    const finalCostLabelPairs = [{ cost: "cost-center", label: values.costCenterValue }, ...values.costLabelPairs.map(({ cost, label }) => ({ cost, label }))];

                    const detailsPayload: any = {
                        projectId: values.projectId,
                        location: values.location,
                        description: values.description,
                    };

                    const commonInput = {
                        name: values.name,
                        cloudProvider: values.cloudProvider,
                        environment: values.environment,
                        costLabelPairs: finalCostLabelPairs,
                        details: { dataplexLake: detailsPayload }, // Changed field name
                    };

                    const mutationString = isEdit ? `mutation UpdateSolutionDataplexLake($input: UpdateSolutionInput) { updateSolutionDataplexLake(input: $input) { id } }` : `mutation CreateSolutionDataplexLake($input: CreateSolutionInput) { createSolutionDataplexLake(input: $input) { id } }`; // Changed mutation names
                    
                    const variables = { input: isEdit ? { id, ...commonInput } : commonInput };
                    const resultKey = isEdit ? 'updateSolutionDataplexLake' : 'createSolutionDataplexLake'; // Changed result keys

                    try {
                        const data = await queryGraphql(mutationString, variables);
                        if (data[resultKey] && data[resultKey].id) {
                            navigate("/datasolutions/data-lakes"); // Changed navigation path
                        } else {
                            setStatus({ error: `Failed to ${isEdit ? 'update' : 'add'} Data Lake. ${JSON.stringify(data.errors)}` }); // Changed text
                        }
                    } catch (error: any) {
                        setStatus({ error: error.message || "An unexpected error occurred." });
                    } finally {
                        setIsSubmittingForm(false);
                        setSubmitting(false);
                    }
                }}
            >
                {({ handleChange, handleBlur, values, setFieldValue, errors, touched, isValid, dirty, status, isSubmitting }) => (
                    <Form>
                        <DynamicFieldsUpdater isEdit={isEdit} initialFormValuesForEdit={initialFormValuesForEdit} configData={configData} setDynamicFields={setDynamicFields} />
                        
                        <Typography fontFamily="Gilroy-Medium" fontSize="16px" mb={2.5}>
                            {isEdit ? "Edit the Data Lake details below" : "Fill the below details to add a Data Lake"} {/* Changed text */}
                        </Typography>
                        
                        <Box sx={{ backgroundColor: "white", p: "24px", borderRadius: "8px", boxShadow: "0px 4px 12px rgba(0,0,0,0.05)", mb: "24px" }}>
                            <Typography fontFamily="Gilroy-Medium" fontSize="14px" sx={{ mb: "16px" }}>Cloud Provider Details</Typography>
                            <Divider sx={{ mb: "24px" }} />
                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <CompulsoryFieldsLabel fieldName="Cloud Provider" />
                                    <ToggleButtonGroup
                                            value={values.cloudProvider}
                                            exclusive
                                            onChange={(event, newProvider) => {
                                                if (newProvider !== null) {
                                                    setFieldValue('cloudProvider', newProvider);
                                                }
                                            }}
                                            aria-label="cloud provider"
                                            sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}
                                        >
                                            {providerDisplayData.map((p) => (
                                                <ToggleButton
                                                    key={p.value}
                                                    value={p.value}
                                                    disabled={p.disabled}
                                                    aria-label={p.label}
                                                    sx={{ display: 'flex', gap: 1, alignItems: 'center', textTransform: 'none', fontFamily: 'Gilroy-Medium', borderRadius: '24px !important', border: '1px solid #ccc !important', px: 2, py: 1, '&.Mui-selected': { borderColor: '#000 !important', backgroundColor: 'rgba(0, 0, 0, 0.08)', }, '&.Mui-selected:hover': { backgroundColor: 'rgba(0, 0, 0, 0.12)', } }}
                                                >
                                                    <img src={p.icon} alt={`${p.label} icon`} style={{ width: '20px', height: '20px' }} />
                                                    {p.label}
                                                </ToggleButton>
                                            ))}
                                        </ToggleButtonGroup>
                                </Grid>
                            </Grid>
                        </Box>

                        <Box sx={{ backgroundColor: "white", p: "24px", borderRadius: "8px", boxShadow: "0px 4px 12px rgba(0,0,0,0.05)", mb: "24px" }}>
                            <Typography fontFamily="Gilroy-Medium" fontSize="14px" sx={{ mb: "16px" }}>Data Lake Details</Typography> {/* Changed text */}
                            <Divider sx={{ mb: "24px" }} />
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName="Data Lake Name" /><TextField variant="filled" fullWidth name="name" value={values.name} onChange={handleChange} onBlur={handleBlur} placeholder="Name" size="medium" hiddenLabel error={touched.name && Boolean(errors.name)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }} /><div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="name" /></div></Grid>
                                <Grid item xs={12} sm={6}>
                                    <CompulsoryFieldsLabel fieldName="Environment" />
                                    <TextField select name="environment" value={values.environment} onChange={handleChange} onBlur={handleBlur} variant="filled" fullWidth hiddenLabel size="medium" error={touched.environment && Boolean(errors.environment)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }} SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 250 } } }, }}>
                                        <MenuItem value="dev" sx={{ fontFamily: "Gilroy-Medium" }}>dev</MenuItem>
                                        <MenuItem value="staging" sx={{ fontFamily: "Gilroy-Medium" }}>staging</MenuItem>
                                        <MenuItem value="prod" sx={{ fontFamily: "Gilroy-Medium" }}>prod</MenuItem>
                                    </TextField><div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="environment" /></div></Grid>
                                <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName={dynamicFields.labels.projectId} /><TextField variant="filled" fullWidth name="projectId" value={values.projectId} onChange={handleChange} onBlur={handleBlur} placeholder={`Enter ${dynamicFields.labels.projectId}`} size="medium" hiddenLabel error={touched.projectId && Boolean(errors.projectId)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }} /><div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="projectId" /></div></Grid>
                                <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName="Location" /><TextField select name="location" value={values.location} onChange={(e) => setFieldValue("location", e.target.value)} onBlur={handleBlur} variant="filled" fullWidth hiddenLabel size="medium" error={touched.location && Boolean(errors.location)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }} SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 250 } } }, }}>{dynamicFields.dropdowns.locations.map((r) => (<MenuItem key={r} value={r} sx={{ fontFamily: "Gilroy-Medium" }}>{r}</MenuItem>))}</TextField><div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="location" /></div></Grid>
                                <Grid item xs={12}><CompulsoryFieldsLabel fieldName="Description"/><TextField variant="filled" fullWidth multiline rows={3} name="description" value={values.description} onChange={handleChange} onBlur={handleBlur} placeholder="Enter a description for the data lake" size="medium" hiddenLabel error={touched.description && Boolean(errors.description)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }} /><div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="description" /></div></Grid>
                            </Grid>
                        </Box>

                        {/* Removed Discovery Specifications section as it's not applicable to Data Lakes */}

                        <Box sx={{ backgroundColor: "white", p: "24px", borderRadius: "8px", boxShadow: "0px 4px 12px rgba(0,0,0,0.05)", mb: "24px" }}>
                            <Typography fontFamily="Gilroy-Medium" fontSize="14px" sx={{ mb: "16px" }}>Labels</Typography>
                            <Divider sx={{ mb: "24px" }} />
                            <Grid container spacing={3} sx={{ mb: "16px" }}>
                                <Grid item xs={12} sm={6}><Typography gutterBottom fontFamily="Gilroy-Regular" fontSize="14px" ml="4px">Key</Typography><TextField fullWidth value="cost-center" disabled variant="filled" hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px", backgroundColor: "#f0f0f0", fontFamily: "Gilroy-Medium" } }} /></Grid>
                                <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName="Value (Cost Center)" /><TextField fullWidth name="costCenterValue" value={values.costCenterValue} onChange={handleChange} onBlur={handleBlur} placeholder="Enter cost center value" variant="filled" hiddenLabel error={touched.costCenterValue && Boolean(errors.costCenterValue)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }} /><div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="costCenterValue" /></div></Grid>
                            </Grid>
                            <FieldArray
                                name="costLabelPairs"
                                render={arrayHelpers => (
                                    <div>
                                        {values.costLabelPairs.map((pair, index) => (
                                            <Grid container spacing={3} key={pair.id || index} sx={{ mb: "16px" }}>
                                                <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName="Key" /><TextField fullWidth name={`costLabelPairs.${index}.cost`} value={pair.cost} onChange={handleChange} onBlur={handleBlur} placeholder="Enter custom key" variant="filled" hiddenLabel error={!!getIn(touched, `costLabelPairs[${index}].cost`) && !!getIn(errors, `costLabelPairs[${index}].cost`)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }} /><div style={{ color: "red", marginTop: "8px" }}><ErrorMessage name={`costLabelPairs.${index}.cost`} /></div></Grid>
                                                <Grid item xs={12} sm={5}><CompulsoryFieldsLabel fieldName="Value" /><TextField fullWidth name={`costLabelPairs.${index}.label`} value={pair.label} onChange={handleChange} onBlur={handleBlur} placeholder="Enter custom value" variant="filled" hiddenLabel error={!!getIn(touched, `costLabelPairs[${index}].label`) && !!getIn(errors, `costLabelPairs[${index}].label`)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }} /><div style={{ color: "red", marginTop: "8px" }}><ErrorMessage name={`costLabelPairs.${index}.label`} /></div></Grid>
                                                <Grid item xs={12} sm={1} sx={{ display: 'flex', alignItems: 'center', pt: { xs: 1, sm: '32px' } }}><IconButton onClick={() => arrayHelpers.remove(index)}><img alt="remove-icon" src="/assets/close-icon.svg" /></IconButton></Grid>
                                            </Grid>
                                        ))}
                                        <Button variant="text" sx={{ color: "#000000", textTransform: "none", fontFamily: "Gilroy-Medium" }} onClick={() => arrayHelpers.push({ cost: "", label: "", id: `new-${Math.random()}` })}>+ Add Key Value Pair</Button>
                                    </div>
                                )}
                            />
                            <FormControlLabel control={<Checkbox checked={confirmationChecked} onChange={(e) => setConfirmationChecked(e.target.checked)} name="confirmationCheckbox" sx={{ color: 'black', '&.Mui-checked': { color: 'black', },mt:"1" }} />} label="I confirm that the details provided are correct and I wish to proceed." sx={{ mb:"24px", display: 'flex', justifyContent: 'flex-start', fontFamily: "Gilroy-Medium", '& .MuiTypography-root': {fontFamily: "Gilroy-Medium"} }} />
                        </Box>
                        
                        <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
                            <Button sx={{ color: "black", borderColor: "black", fontWeight: "bold", textTransform: "none", px: 4, fontFamily: "Gilroy-Bold", '&:hover': { borderColor: 'black', backgroundColor: 'rgba(0,0,0,0.04)' } }} variant="outlined" size="large" onClick={() => navigate("/datasolutions/data-lakes")} disabled={isSubmittingForm || isSubmitting}>Cancel</Button> {/* Changed navigation path */}
                            <Button sx={{ backgroundColor: "black", color: "white", fontWeight: "bold", textTransform: "none", px: 4, fontFamily: "Gilroy-Bold", "&:hover": { backgroundColor: "#333333" }, "&.Mui-disabled": { backgroundColor: "#cccccc", color: "#888888" } }} variant="contained" size="large" type="submit" disabled={isSubmittingForm || isSubmitting || !(isValid && dirty && confirmationChecked)  || isViewer }>{(isSubmittingForm || isSubmitting) ? <CircularProgress size={24} color="inherit" /> : (isEdit ? "Save Changes" : "Add New Data Lake")}</Button> {/* Changed text */}
                        </Box>
                    </Form>
                )}
            </Formik>
        </Container>
    );
};

// --- Page Component (Wrapper) ---
const AddDataLakesPage: React.FC<AddDataLakesPageProps> = ({ isEdit }) => { // Changed component name
    const navigate = useNavigate();
    const theme = useTheme();
    const getInitialDrawerState = () => {
        if (typeof window !== 'undefined') {
            const storedState = localStorage.getItem('isFaradayDrawerOpen');
            return storedState !== null ? JSON.parse(storedState) : true;
        }
        return true;
    };
    const [isDrawerActuallyOpen, setIsDrawerActuallyOpen] = useState<boolean>(getInitialDrawerState);

    const handleDrawerStateChange = (isOpenFromDrawer: boolean) => {
        setIsDrawerActuallyOpen(isOpenFromDrawer);
        if (typeof window !== 'undefined') {
            localStorage.setItem('isFaradayDrawerOpen', JSON.stringify(isOpenFromDrawer));
        }
    };
    
    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <MainDrawer index={"datasolutions-data-lakes"} onStateChange={handleDrawerStateChange} /> {/* Changed index */}
            <Box component="main" sx={{ flexGrow: 1, bgcolor: '#fafafa', transition: theme.transitions.create('margin', { easing: theme.transitions.easing.sharp, duration: theme.transitions.duration.enteringScreen, }), overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ mb: 3, ml: {xs:2, sm:3, md:5} , mt:2 }}>
                    <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "#555555", fontFamily: "Gilroy-Medium" }}>
                        <Link underline="hover" color="inherit" href="/datasolutions/data-lakes" sx={{ fontFamily: "Gilroy-Medium" }} onClick={(e)=> {e.preventDefault(); navigate("/datasolutions/data-lakes")}}>Data Lakes</Link> {/* Changed text and navigation path */}
                        <Typography color="text.primary" sx={{ fontFamily: "Gilroy-Medium" }}>{isEdit ? "Edit Data Lake" : "Add New Data Lake"}</Typography> {/* Changed text */}
                    </Breadcrumbs>
                    <Box display="flex" alignItems="center" mt={1.5}>
                        <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate("/datasolutions/data-lakes")} sx={{ mr: 0.5 }}><ArrowBackIosNewIcon fontSize="small" /></IconButton> {/* Changed navigation path */}
                        <Typography fontFamily="Gilroy-Bold" fontSize="20px">{isEdit ? "Edit Data Lake" : "Add New Data Lake"}</Typography> {/* Changed text */}
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'center', flexGrow: 1, alignItems: 'flex-start', pt:0 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', width: { xs: '95%', md: '80%' }, py: 0 }}>
                        <DataLakesFormContent isEdit={isEdit} /> {/* Changed component */}
                    </Box>
                </Box>
                <MiniBot />
            </Box>
        </Box>
    );
};

export default AddDataLakesPage;